import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { DocumentViewService } from '../../shared';

export interface DocumentTracingData {
  current_document: string;
  document_type?: string; // Add document type field
  purchase_orders?: { [key: string]: number };
  requisition?: { [key: string]: number }; // Add requisition field for PO tracing
  grns?: { [key: string]: number }; // Make grns optional
  quality_checks?: { [key: string]: number }; // Make quality_checks optional
  creation_vouchers: { [key: string]: string };
  posted_vouchers: { [key: string]: number }; // Changed from any[] to object
}

export interface DocumentTracingCallbacks {
  viewRequisition?: (id: any) => void;
  viewPurchaseOrder?: (id: any) => void;
  viewGRN?: (id: any) => void;
  viewQualityCheck?: (id: any) => void;
  viewVoucher?: (id: any) => void;
  viewPostedVoucher?: (id: any) => void;
}

@Component({
  selector: 'app-document-tracing-modal',
  templateUrl: './document-tracing-modal.component.html',
  styleUrls: ['./document-tracing-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatDialogModule
  ]
})
export class DocumentTracingModalComponent implements OnInit {
  tracingData: DocumentTracingData | null = null;
  loading = false;

  constructor(
    public dialogRef: MatDialogRef<DocumentTracingModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private documentViewService: DocumentViewService
  ) {}

  ngOnInit(): void {
    if (this.data) {
      this.tracingData = this.data.tracingData;
      this.loading = this.data.loading || false;
    }
    
    // Auto-close loading dialog after 30 seconds if something goes wrong
    if (this.loading) {
      setTimeout(() => {
        if (this.loading) {
          this.closeDialog();
        }
      }, 30000);
    }
    
    // Log available document types for debugging
    console.log('Available document types:', this.documentViewService.getSupportedDocumentTypes());
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  getPurchaseOrderCount(): number {
    let count = 0;
    if (this.tracingData?.purchase_orders) {
      count += Object.keys(this.tracingData.purchase_orders).length;
    }
    if (this.tracingData?.requisition) {
      count += Object.keys(this.tracingData.requisition).length;
    }
    return count;
  }

  getGrnCount(): number {
    return this.tracingData && this.tracingData.grns ? Object.keys(this.tracingData.grns).length : 0;
  }

  getQualityCheckCount(): number {
    return this.tracingData && this.tracingData.quality_checks ? Object.keys(this.tracingData.quality_checks).length : 0;
  }

  getVoucherCount(): number {
    return this.tracingData && this.tracingData.creation_vouchers ? Object.keys(this.tracingData.creation_vouchers).length : 0;
  }

  getPostedVoucherCount(): number {
    return this.tracingData && this.tracingData.posted_vouchers ? Object.keys(this.tracingData.posted_vouchers).length : 0;
  }

  // Helper methods for timeline display
  getPurchaseOrders(): Array<{key: string, value: number}> {
    const result: Array<{key: string, value: number}> = [];
    
    if (this.tracingData?.purchase_orders) {
      result.push(...Object.entries(this.tracingData.purchase_orders).map(([key, value]) => ({ key, value })));
    }
    if (this.tracingData?.requisition) {
      result.push(...Object.entries(this.tracingData.requisition).map(([key, value]) => ({ key, value })));
    }
    
    return result;
  }

  getGrns(): Array<{key: string, value: number}> {
    if (!this.tracingData || !this.tracingData.grns) return [];
    return Object.entries(this.tracingData.grns).map(([key, value]) => ({ key, value }));
  }

  getQualityChecks(): Array<{key: string, value: number}> {
    if (!this.tracingData || !this.tracingData.quality_checks) return [];
    return Object.entries(this.tracingData.quality_checks).map(([key, value]) => ({ key, value }));
  }

  getCreationVouchers(): Array<{key: string, value: string}> {
    if (!this.tracingData || !this.tracingData.creation_vouchers) return [];
    return Object.entries(this.tracingData.creation_vouchers).map(([key, value]) => ({ key, value }));
  }

  getPostedVouchers(): Array<{key: string, value: number}> {
    if (!this.tracingData || !this.tracingData.posted_vouchers) return [];
    return Object.entries(this.tracingData.posted_vouchers).map(([key, value]) => ({ key, value }));
  }

  hasAnyDocuments(): boolean {
    return this.getPurchaseOrderCount() > 0 || 
           this.getGrnCount() > 0 || 
           this.getQualityCheckCount() > 0 || 
           this.getVoucherCount() > 0 || 
           this.getPostedVoucherCount() > 0;
  }

  // Get document type for display
  getDocumentTypeDisplay(): string {
    if (!this.tracingData?.document_type) return 'Document';
    
    switch (this.tracingData.document_type.toLowerCase()) {
      case 'requisition':
        return 'Requisition';
      case 'purchase_order':
        return 'Purchase Order';
      case 'grn':
        return 'Goods Receipt Note';
      case 'quality_check':
        return 'Quality Check';
      default:
        return 'Document';
    }
  }

  // Get all document types that have data
  getAvailableDocumentTypes(): string[] {
    const types: string[] = [];
    
    if (this.tracingData?.requisition && Object.keys(this.tracingData.requisition).length > 0) {
      types.push('requisition');
    }
    
    if (this.tracingData?.purchase_orders && Object.keys(this.tracingData.purchase_orders).length > 0) {
      types.push('purchase_orders');
    }
    
    if (this.getGrnCount() > 0) types.push('grn');
    if (this.getQualityCheckCount() > 0) types.push('quality_check');
    if (this.getVoucherCount() > 0) types.push('creation_voucher');
    if (this.getPostedVoucherCount() > 0) types.push('posted_voucher');
    
    return types;
  }

  // Get document count for any type
  getDocumentCount(type: string): number {
    switch (type) {
      case 'requisition':
        return this.tracingData?.requisition ? Object.keys(this.tracingData.requisition).length : 0;
      case 'purchase_orders':
        return this.tracingData?.purchase_orders ? Object.keys(this.tracingData.purchase_orders).length : 0;
      case 'grn':
        return this.getGrnCount();
      case 'quality_check':
        return this.getQualityCheckCount();
      case 'creation_voucher':
        return this.getVoucherCount();
      case 'posted_voucher':
        return this.getPostedVoucherCount();
      default:
        return 0;
    }
  }

  // Get documents for any type
  getDocuments(type: string): any[] {
    switch (type) {
      case 'requisition':
        return this.tracingData?.requisition ? Object.entries(this.tracingData.requisition).map(([key, value]) => ({ key, value })) : [];
      case 'purchase_orders':
        return this.tracingData?.purchase_orders ? Object.entries(this.tracingData.purchase_orders).map(([key, value]) => ({ key, value })) : [];
      case 'grn':
        return this.getGrns();
      case 'quality_check':
        return this.getQualityChecks();
      case 'creation_voucher':
        return this.getCreationVouchers();
      case 'posted_voucher':
        return this.tracingData?.posted_vouchers ? Object.entries(this.tracingData.posted_vouchers).map(([key, value]) => ({ key, value })) : [];
      default:
        return [];
    }
  }

  // Get display title for any document type
  getDocumentTypeTitle(type: string): string {
    switch (type) {
      case 'requisition':
        return 'Requisitions';
      case 'purchase_orders':
        return 'Purchase Orders';
      case 'grn':
        return 'Goods Receipt Notes';
      case 'quality_check':
        return 'Quality Check Documents';
      case 'creation_voucher':
        return 'Creation Vouchers';
      case 'posted_voucher':
        return 'Posted Vouchers';
      default:
        return 'Documents';
    }
  }

  // Get status badge for any document type
  getDocumentStatusBadge(type: string): { text: string, class: string } {
    switch (type) {
      case 'requisition':
        return { text: 'Approved', class: 'approved' };
      case 'purchase_orders':
        return { text: 'Approved', class: 'approved' };
      case 'grn':
        return { text: 'Received', class: 'received' };
      case 'quality_check':
        return { text: 'Passed', class: 'passed' };
      case 'creation_voucher':
        return { text: 'Created', class: 'created' };
      case 'posted_voucher':
        return { text: 'Posted', class: 'posted' };
      default:
        return { text: 'Active', class: 'active' };
    }
  }

  // Get icon for any document type
  getDocumentIcon(type: string): string {
    switch (type) {
      case 'requisition':
        return 'description';
      case 'purchase_orders':
        return 'shopping_cart';
      case 'grn':
        return 'local_shipping';
      case 'quality_check':
        return 'verified';
      case 'creation_voucher':
        return 'edit';
      case 'posted_voucher':
        return 'check_circle';
      default:
        return 'description';
    }
  }

  // Handle document click to view specific document
  viewDocument(documentType: string, documentKey: string, documentValue: any): void {
    console.log(`Viewing ${documentType}: ${documentKey} - ${documentValue}`);
    
    // Map document types to the service document types
    const documentTypeMapping: { [key: string]: string } = {
      'requisition': 'requisition',
      'purchase_orders': 'purchase_order',
      'grn': 'grn',
      'quality_check': 'quality_check',
      'creation_voucher': 'voucher',
      'posted_voucher': 'voucher_posting'
    };
    
    const serviceDocumentType = documentTypeMapping[documentType];
    
    if (serviceDocumentType && this.documentViewService.isDocumentTypeSupported(serviceDocumentType)) {
      // Use the DocumentViewService to view the document
      this.documentViewService.view({
        document_id: documentValue,
        document_type: serviceDocumentType
      });
      
      // Close the tracing modal after opening the document
      // this.closeDialog();
    } else {
      console.error(`Document type '${documentType}' is not supported or mapped incorrectly`);
      
      // Fallback to callbacks if they exist (for backward compatibility)
      const callbacks = (this.data as any)?.callbacks || {};
      this.fallbackToCallbacks(documentType, documentValue, callbacks);
    }
  }

  // Fallback method for backward compatibility with callbacks
  private fallbackToCallbacks(documentType: string, documentValue: any, callbacks: any): void {
    switch (documentType) {
      case 'requisition':
        if (callbacks.viewRequisition) {
          callbacks.viewRequisition(documentValue);
        } else {
          console.log('No viewRequisition callback provided');
          this.showServiceUnavailableMessage(documentType);
        }
        break;
      case 'purchase_orders':
        if (callbacks.viewPurchaseOrder) {
          callbacks.viewPurchaseOrder(documentValue);
        } else {
          console.log('No viewPurchaseOrder callback provided');
          this.showServiceUnavailableMessage(documentType);
        }
        break;
      case 'grn':
        if (callbacks.viewGRN) {
          callbacks.viewGRN(documentValue);
        } else {
          console.log('No viewGRN callback provided');
          this.showServiceUnavailableMessage(documentType);
        }
        break;
      case 'quality_check':
        if (callbacks.viewQualityCheck) {
          callbacks.viewQualityCheck(documentValue);
        } else {
          console.log('No viewQualityCheck callback provided');
          this.showServiceUnavailableMessage(documentType);
        }
        break;
      case 'creation_voucher':
        if (callbacks.viewVoucher) {
          callbacks.viewVoucher(documentValue);
        } else {
          console.log('No viewVoucher callback provided');
          this.showServiceUnavailableMessage(documentType);
        }
        break;
      case 'posted_voucher':
        if (callbacks.viewPostedVoucher) {
          callbacks.viewPostedVoucher(documentValue);
        } else {
          console.log('No viewPostedVoucher callback provided');
          this.showServiceUnavailableMessage(documentType);
        }
        break;
      default:
        console.log(`Unknown document type: ${documentType}`);
        this.showServiceUnavailableMessage(documentType);
    }
  }

  // Show message when service is unavailable
  private showServiceUnavailableMessage(documentType: string): void {
    const documentTypeTitle = this.getDocumentTypeTitle(documentType);
    console.warn(`Document viewing service unavailable for ${documentTypeTitle}. Please check if the service is properly configured.`);
    
    // You can add user notification here if needed
    // For example, show a snackbar or alert
  }
} 